import { validateNif } from './employeeInviteValidation';

/**
 * Tiene FORMA de documento español: 8 dígitos y letra (DNI), o X/Y/Z, 7 dígitos y letra
 * (NIE). Solo la forma; si la letra de control cuadra lo dice `validateNif`.
 */
const FORMA_ESPANOLA = /^(\d{8}[A-Z]|[XYZ]\d{7}[A-Z])$/;

/**
 * Cualquier otro documento: alfanumérico, con al menos un dígito.
 *
 * 🔴 A propósito NO se apuesta por la forma exacta. En producción hay personas de empresas
 * extranjeras dadas de alta con el documento de su país, y cada país tiene el suyo: el BSN
 * holandés son 9 dígitos sin ninguna letra, el personnummer sueco 10 o 12, y el codice
 * fiscale italiano mezcla letras y dígitos de otra manera. Una expresión calibrada sobre
 * los tres casos que hoy hay en la base dejaría fuera al cuarto.
 *
 * El dígito obligatorio es lo único que se exige: descarta un correo, una frase o unas
 * letras sueltas sin descartar ningún documento real.
 */
const OTRO_DOCUMENTO = /^(?=.*\d)[A-Z0-9]{6,14}$/;

/** Se copia del propio documento, donde el número va con guiones, puntos o espacios. */
const SEPARADORES = /[\s.-]/g;

/**
 * Deja el identificador como hay que compararlo y como hay que mandarlo.
 *
 * 🔴 Lo usan los DOS lados —el que valida y el que envía— y tiene que seguir siendo así.
 * El backend no quita separadores: compara `upper(nif)` contra lo que hay guardado. Si se
 * validara el valor limpio y se mandara el crudo, quien pega «74.375.408-D» vería que la
 * pantalla da el formato por bueno y el backend le contestaría «credenciales incorrectas»,
 * que es justo la confusión que esta validación existe para quitar.
 */
export const normalizeLoginIdentifier = (identifier: string): string =>
	(identifier || '').trim().toUpperCase().replace(SEPARADORES, '');

/**
 * ¿Merece la pena gastar un intento de login con esto?
 *
 * Son dos preguntas distintas según lo que se haya escrito:
 *
 *   · Si tiene forma de DNI o NIE **se exige la letra de control**. Ahí el aviso temprano
 *     caza el dedazo, y sabemos que no deja fuera a nadie: de los 570 NIF de usuarios
 *     activos en producción, ninguno la tiene mal.
 *   · Si no la tiene, es el documento de otro país y **decide el backend**, que es quien
 *     sabe si esa persona existe.
 *
 * 🔴 No confundir con `validateNif`, que se usa al dar de alta plantilla: allí hay que ser
 * estricto, porque un NIF mal escrito se arrastra hasta Caser. Aquí pasarse de estricto
 * sale más caro que quedarse corto: quien escribe mal su NIF pierde un intento, pero a
 * quien rechazamos de más lo dejamos fuera de su cuenta.
 */
export const isValidLoginIdentifier = (identifier: string): boolean => {
	const limpio = normalizeLoginIdentifier(identifier);

	return FORMA_ESPANOLA.test(limpio) ? validateNif(limpio) : OTRO_DOCUMENTO.test(limpio);
};
